/**
 * ControlPanel API (`/master-history`) クライアント (マスタ変更履歴 SBI 7)。
 *
 * 履歴自体は読み取り専用 (追記専用)。画面の 3 階層に対応して、参照のメソッドを 2 組に分けている。
 * - ① 変更一覧 / ② 対象データの履歴一覧: `list` / `listByEntity` (一覧用。before / after / changes を含まない)
 * - ③ 履歴詳細 / 最新との比較: `get` / `diff` (差分とスナップショットを含む)
 * - 復元: `restore` (その版の内容を元のマスタへ書き戻す。結果は新しい版として履歴に残る)
 *
 * `entity_key` は日本語や `#` を含むため、パスではなくクエリで渡す (`encodeURIComponent`)。
 * `URLSearchParams` は空白を `+` にしてしまうため、他のクライアントと同じ組み立て方にする。
 *
 * ⚠️ 最終的な認可は常にバックエンド (403)。マスタ種別ごとの画面の出し分けは利便性のため。
 */
import { adminRequest, encodeSegment, type AdminClientOptions } from './adminRequest';
import type {
  MasterHistoryDetail,
  MasterHistoryDiff,
  MasterHistoryEntityListParams,
  MasterHistoryListParams,
  MasterHistoryListResponse,
  MasterHistoryRestoreResult,
} from './types';

export type MasterHistoryClientOptions = AdminClientOptions;

/** 値が未指定 (undefined / 空文字) のものを除いて、`?k=v&k=v` を組み立てる。空なら空文字。 */
function buildQuery(
  entries: ReadonlyArray<readonly [string, string | number | undefined]>,
): string {
  const parts: string[] = [];
  for (const [key, value] of entries) {
    if (value === undefined || value === '') continue;
    parts.push(`${key}=${encodeURIComponent(String(value))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export class MasterHistoryClient {
  private readonly options: MasterHistoryClientOptions;

  constructor(options: MasterHistoryClientOptions) {
    this.options = options;
  }

  /**
   * GET /master-history (① 変更一覧)
   *
   * `masterType` を省略すると、呼び出し元が権限を持つ全種別が対象になる。
   * 権限のない種別を指定すると 403。
   */
  async list(params: MasterHistoryListParams = {}): Promise<MasterHistoryListResponse> {
    const query = buildQuery([
      ['master_type', params.masterType],
      ['action', params.action],
      ['changed_from', params.changedFrom],
      ['changed_to', params.changedTo],
      ['actor', params.actor],
      ['route_id', params.routeId],
      ['limit', params.limit],
      ['next_token', params.nextToken],
    ]);
    return adminRequest<MasterHistoryListResponse>(this.options, 'GET', `master-history${query}`);
  }

  /**
   * GET /master-history?entity_key= (② 対象データの履歴一覧)
   *
   * 削除済みの対象も取得できる。履歴が 1 件も無い対象は 200 で空配列が返る (404 にならない)。
   * `entityKey` は一覧の各項目の `entity_key` をそのまま渡す。
   */
  async listByEntity(
    entityKey: string,
    params: MasterHistoryEntityListParams = {},
  ): Promise<MasterHistoryListResponse> {
    const query = buildQuery([
      ['entity_key', entityKey],
      ['limit', params.limit],
      ['next_token', params.nextToken],
    ]);
    return adminRequest<MasterHistoryListResponse>(this.options, 'GET', `master-history${query}`);
  }

  /** GET /master-history/{version}?entity_key= (③ 履歴詳細。存在しない版は 404) */
  async get(version: string, entityKey: string): Promise<MasterHistoryDetail> {
    const query = buildQuery([['entity_key', entityKey]]);
    return adminRequest<MasterHistoryDetail>(
      this.options,
      'GET',
      `master-history/${encodeSegment(version)}${query}`,
    );
  }

  /**
   * GET /master-history/diff?entity_key=&from=&to=latest (この版と最新との比較)
   *
   * 差分の計算はサーバ側。`to` は `latest` 固定で送る (API は `latest` のみ受け付ける)。
   */
  async diff(entityKey: string, fromVersion: string): Promise<MasterHistoryDiff> {
    const query = buildQuery([
      ['entity_key', entityKey],
      ['from', fromVersion],
      ['to', 'latest'],
    ]);
    return adminRequest<MasterHistoryDiff>(this.options, 'GET', `master-history/diff${query}`);
  }

  /**
   * POST /master-history/{version}/restore?entity_key= (この版の内容へ復元)
   *
   * 参照先 (チャイム・共有テキスト・音源など) が無いときは 409 (利用者向けの日本語 `message` つき)。
   * このとき本体は変更されない。
   */
  async restore(version: string, entityKey: string): Promise<MasterHistoryRestoreResult> {
    const query = buildQuery([['entity_key', entityKey]]);
    return adminRequest<MasterHistoryRestoreResult>(
      this.options,
      'POST',
      `master-history/${encodeSegment(version)}/restore${query}`,
      {},
    );
  }
}
